// Hybrid-synthesis research task contracts (#4653).
//
// A research task runs as a SIBLING of the governed answer path: governed
// clauses compute unchanged, research synthesizes external source-cited
// content, and the API composes the two once. These contracts are the seam
// between the research runner (apps/api/src/ask-helix/research/) and the
// research treatment (ResearchSynthesisSection, apps/web).
//
// Binding laws carried by this contract:
// - Research content NEVER mints governed numbers. Cited figures live inside
//   citation `quotedClaim` strings and render only inside the research
//   treatment; they are never numeric bindings and never merge with governed
//   values.
// - Every applicability judgment ("applies to you / does not apply") must
//   name the governed fact refs it was judged against.
// - Research failure preserves the governed answer and names the gap; there
//   is no async upgrade state and no partial research render.
// - Promotion of any research artifact into governed data (`industry_reference`)
//   is a separate approval flow; nothing here admits research into governed
//   evidence.

export const ASK_HELIX_RESEARCH_TASK_SCHEMA_VERSION =
  'ask-helix-research-task/v1' as const;

/**
 * One external source backing a research claim. `quotedClaim` is the claim AS
 * THE SOURCE STATES IT — including any figures — and is presentation-local
 * research text, never a governed numeric binding.
 */
export type AskHelixResearchCitationDto = {
  sourceUrl: string;
  sourceTitle: string;
  /** ISO-8601 timestamp of when the source was accessed. */
  accessedAt: string;
  quotedClaim: string;
};

export const ASK_HELIX_RESEARCH_APPLICABILITY = [
  'applies',
  'does_not_apply',
  'indeterminate',
] as const;

export type AskHelixResearchApplicabilityDto =
  (typeof ASK_HELIX_RESEARCH_APPLICABILITY)[number];

/**
 * Structured confrontation of one research claim against governed facts.
 * `governedFactRefs` are opaque references to governed facts (compiled-context
 * fact ids), not values: they let the composer narrate "applies to you because
 * <governed fact>" without research ever carrying governed content.
 */
export type AskHelixResearchConfrontationJudgmentDto = {
  researchClaim: string;
  citations: AskHelixResearchCitationDto[];
  applicability: AskHelixResearchApplicabilityDto;
  /** Required when applicability is `applies` or `does_not_apply`. */
  governedFactRefs: string[];
  rationale: string;
};

/**
 * Sibling-task request. `researchQuestion` is the out-of-governed-realm clause
 * text. `confrontationFactRefs` is the read-only projection of governed fact
 * refs the research may confront against — it is the ONLY governed content
 * the task receives, and the task cannot alter it.
 */
export type AskHelixResearchTaskRequestDto = {
  schemaVersion: typeof ASK_HELIX_RESEARCH_TASK_SCHEMA_VERSION;
  tenantId: string;
  researchQuestion: string;
  confrontationFactRefs: string[];
  /** Optional per-task timeout override; the runner clamps it deterministically. */
  timeoutMs?: number;
};

/**
 * Failure posture is closed and singular by design: any research failure
 * preserves the governed answer unchanged and names the research gap. Adding
 * a posture is a design decision, not a local convenience.
 */
export const ASK_HELIX_RESEARCH_FAILURE_POSTURES = [
  'governed_answer_preserved',
] as const;

export type AskHelixResearchFailurePostureDto =
  (typeof ASK_HELIX_RESEARCH_FAILURE_POSTURES)[number];

export const ASK_HELIX_RESEARCH_TASK_STATUSES = [
  'completed',
  'timed_out',
  'failed',
  'unavailable',
] as const;

export type AskHelixResearchTaskStatusDto =
  (typeof ASK_HELIX_RESEARCH_TASK_STATUSES)[number];

export type AskHelixResearchTaskSuccessDto = {
  schemaVersion: typeof ASK_HELIX_RESEARCH_TASK_SCHEMA_VERSION;
  status: 'completed';
  /** Research prose. Presentation-local; carries no governed numeric bindings. */
  synthesis: string;
  citations: AskHelixResearchCitationDto[];
  confrontations: AskHelixResearchConfrontationJudgmentDto[];
};

export type AskHelixResearchTaskFailureDto = {
  schemaVersion: typeof ASK_HELIX_RESEARCH_TASK_SCHEMA_VERSION;
  status: 'timed_out' | 'failed' | 'unavailable';
  posture: AskHelixResearchFailurePostureDto;
  /** Executive-language naming of the gap (e.g. "external research unavailable"). */
  gapSummary: string;
};

export type AskHelixResearchTaskResultDto =
  | AskHelixResearchTaskSuccessDto
  | AskHelixResearchTaskFailureDto;

/**
 * Wire payload for the closed-registry ResearchSynthesisSection component.
 * The badge copy is server-authored; the web renders it verbatim and never
 * re-adjudicates provenance.
 */
export type ResearchSynthesisSectionPropsV1 = {
  title: string;
  synthesis: string;
  citations: AskHelixResearchCitationDto[];
  confrontations: AskHelixResearchConfrontationJudgmentDto[];
  badgeLabel: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isHttpUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return /^https?:\/\/\S+$/i.test(value.trim());
}

export function isAskHelixResearchCitationDto(
  value: unknown,
): value is AskHelixResearchCitationDto {
  return (
    isRecord(value) &&
    isHttpUrl(value.sourceUrl) &&
    isNonEmptyString(value.sourceTitle) &&
    isNonEmptyString(value.accessedAt) &&
    isNonEmptyString(value.quotedClaim)
  );
}

export function isAskHelixResearchConfrontationJudgmentDto(
  value: unknown,
): value is AskHelixResearchConfrontationJudgmentDto {
  if (
    !isRecord(value) ||
    !isNonEmptyString(value.researchClaim) ||
    !Array.isArray(value.citations) ||
    !value.citations.every(isAskHelixResearchCitationDto) ||
    !ASK_HELIX_RESEARCH_APPLICABILITY.includes(
      value.applicability as AskHelixResearchApplicabilityDto,
    ) ||
    !Array.isArray(value.governedFactRefs) ||
    !value.governedFactRefs.every(isNonEmptyString) ||
    !isNonEmptyString(value.rationale)
  ) {
    return false;
  }
  // A decided applicability judgment is only honest when it names the governed
  // facts it was judged against; `indeterminate` may carry none.
  if (value.applicability !== 'indeterminate') {
    return value.governedFactRefs.length > 0;
  }
  return true;
}

/**
 * Runtime admission predicate for the ResearchSynthesisSection V1 payload.
 * Fail-closed: the component renders a named gap, never a best-effort shape.
 */
export function isResearchSynthesisSectionPropsV1(
  value: unknown,
): value is ResearchSynthesisSectionPropsV1 {
  return (
    isRecord(value) &&
    isNonEmptyString(value.title) &&
    isNonEmptyString(value.synthesis) &&
    Array.isArray(value.citations) &&
    value.citations.every(isAskHelixResearchCitationDto) &&
    Array.isArray(value.confrontations) &&
    value.confrontations.every(isAskHelixResearchConfrontationJudgmentDto) &&
    isNonEmptyString(value.badgeLabel)
  );
}
